/**
 * Pick an article to read into question/answer candidates. Each answer stays in the article's words and
 * carries the passage behind it; every candidate is checked as it is made, and none is served until it
 * is promoted.
 */

import { useMemo, useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useGenerateKnowledge, useKnowledgeArticles, useSelectedBuId } from '@/hooks/use-knowledge-promotion';
import { cn, getApiErrorMessage } from '@/lib/utils';

interface GenerateDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export const GenerateDialog = ({ open, onOpenChange }: GenerateDialogProps) => {
	const { data: articles, isLoading } = useKnowledgeArticles();
	const generate = useGenerateKnowledge();
	const buId = useSelectedBuId();
	const [search, setSearch] = useState('');
	const [articleId, setArticleId] = useState<string | null>(null);
	const shown = useMemo(
		() => (articles ?? []).filter((article) => article.name.toLowerCase().includes(search.trim().toLowerCase())).slice(0, 100),
		[articles, search]
	);

	const start = async () => {
		if (!articleId) return;
		try {
			const job = await generate.mutateAsync({ articleId, buId });
			toast.success(`Reading "${job.articleName}". Candidates appear under To review as they are checked.`);
			setArticleId(null);
			onOpenChange(false);
		} catch (error) {
			toast.error(getApiErrorMessage(error, 'Could not start'));
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className='sm:max-w-lg' data-testid='generate-dialog'>
				<DialogHeader>
					<DialogTitle>Generate from a source</DialogTitle>
					<DialogDescription>
						Each question the source answers becomes a candidate, in its own words. Nothing is served until you promote it.
					</DialogDescription>
				</DialogHeader>
				<Input placeholder='Search sources' value={search} onChange={(event) => setSearch(event.target.value)} />
				<div className='flex max-h-72 flex-col gap-0.5 overflow-y-auto rounded-md border p-1' role='listbox' aria-label='Sources'>
					{isLoading && <p className='p-2 text-xs text-muted-foreground'>Loading sources…</p>}
					{!isLoading && shown.length === 0 && <p className='p-2 text-xs text-muted-foreground'>No sources match.</p>}
					{shown.map((article) => (
						<button
							key={article.id}
							type='button'
							role='option'
							aria-selected={articleId === article.id}
							onClick={() => setArticleId(article.id)}
							className={cn(
								'flex items-center gap-2 rounded px-2 py-1.5 text-left text-[13px] hover:bg-muted',
								articleId === article.id && 'bg-muted font-medium'
							)}
						>
							<FileText className='size-3.5 shrink-0 text-muted-foreground' aria-hidden />
							<span className='min-w-0 flex-1 truncate'>{article.name}</span>
							<span className='shrink-0 text-[10px] text-muted-foreground'>{article.type}</span>
						</button>
					))}
				</div>
				<DialogFooter>
					<Button variant='ghost' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button disabled={!articleId || generate.isPending} onClick={start}>
						{generate.isPending && <Loader2 className='size-3.5 animate-spin' aria-hidden />}
						Generate
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};
